import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash, Gear, Tag } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
];

const APPLIES_TO = [
  { value: 'placement', label: 'Placements' },
  { value: 'user', label: 'Users' },
  { value: 'both', label: 'Both' },
];

const CustomFieldsPage = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newField, setNewField] = useState({ field_name: '', field_type: 'text', applies_to: 'placement', options: '', required: false });

  const fetchFields = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/custom-fields`, { withCredentials: true });
      setFields(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFields(); }, [fetchFields]);

  const handleCreate = async () => {
    if (!newField.field_name.trim()) { toast.error('Field name is required'); return; }
    try {
      const payload = {
        ...newField,
        options: newField.field_type === 'select' ? newField.options.split(',').map(o => o.trim()).filter(Boolean) : [],
      };
      await axios.post(`${API_URL}/api/admin/custom-fields`, payload, { withCredentials: true });
      toast.success('Custom field created');
      setNewField({ field_name: '', field_type: 'text', applies_to: 'placement', options: '', required: false });
      setShowForm(false);
      fetchFields();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create field'); }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Delete this custom field? Values stored on records will remain.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/custom-fields/${fieldId}`, { withCredentials: true });
      toast.success('Field deleted');
      fetchFields();
    } catch (e) { toast.error('Failed to delete field'); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="custom-fields-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Custom Fields</h1>
          <p className="text-sm text-slate-500 mt-0.5">Define custom fields for placements and users. Values can be set on individual records.</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} data-testid="add-field-btn" className="bg-blue-600 hover:bg-blue-700">
          <Plus size={16} className="mr-1" /> Add Field
        </Button>
      </div>

      {showForm && (
        <Card className="border-blue-200 bg-blue-50/30" data-testid="create-field-form">
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Field Name *</Label>
                <Input value={newField.field_name} onChange={(e) => setNewField({ ...newField, field_name: e.target.value })} placeholder="e.g. Priority Level" data-testid="field-name-input" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={newField.field_type} onValueChange={(v) => setNewField({ ...newField, field_type: v })}>
                  <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Applies To</Label>
                <Select value={newField.applies_to} onValueChange={(v) => setNewField({ ...newField, applies_to: v })}>
                  <SelectTrigger data-testid="field-applies-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APPLIES_TO.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newField.field_type === 'select' && (
              <div>
                <Label className="text-xs">Options (comma-separated)</Label>
                <Input value={newField.options} onChange={(e) => setNewField({ ...newField, options: e.target.value })} placeholder="e.g. Low, Medium, High, Critical" data-testid="field-options-input" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Checkbox id="field_required" checked={newField.required} onCheckedChange={(v) => setNewField({ ...newField, required: v })} />
              <Label htmlFor="field_required" className="text-xs cursor-pointer">Required field</Label>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate} data-testid="save-field-btn" className="bg-emerald-600 hover:bg-emerald-700">Create Field</Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-10 text-slate-400">Loading...</div>
      ) : fields.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="text-center py-12 text-slate-400">
          <Gear size={32} className="mx-auto mb-3 text-slate-300" />
          <p>No custom fields defined yet. Click "Add Field" to create one.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((f) => (
            <Card key={f.id} className="border-slate-200" data-testid={`field-card-${f.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Tag size={16} className="text-blue-500" />
                    <h3 className="text-sm font-semibold text-slate-800">{f.field_name}</h3>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(f.id)} className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`delete-field-${f.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline" className="text-[10px]">{FIELD_TYPES.find(t => t.value === f.field_type)?.label || f.field_type}</Badge>
                  <Badge className={`text-[10px] ${f.applies_to === 'both' ? 'bg-purple-100 text-purple-700' : f.applies_to === 'user' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}`}>
                    {f.applies_to === 'both' ? 'Placements & Users' : f.applies_to === 'user' ? 'Users' : 'Placements'}
                  </Badge>
                  {f.required && <Badge className="bg-red-100 text-red-600 text-[10px]">Required</Badge>}
                </div>
                {f.options?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {f.options.map((opt, i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-100 rounded text-slate-600">{opt}</span>)}
                  </div>
                )}
                <p className="text-[10px] text-slate-400 mt-2">Created by {f.created_by_name} on {new Date(f.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomFieldsPage;
